import React from 'react';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { formatTestId } from '@/utils/helpers/formatTestId';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg border font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'border-primary bg-primary text-primary-foreground shadow hover:bg-primary/90 hover:border-primary/90 active:bg-primary/80',
        secondary:
          'border-secondary bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:text-secondary-foreground hover:border-primary [&_svg]:brightness-0 [&_svg]:invert',
        danger:
          'border-destructive bg-transparent text-destructive hover:border-foreground hover:text-foreground active:border-foreground/80',
        lightRed:
          'border-destructive/30 bg-destructive/10 text-destructive hover:bg-foreground hover:border-foreground hover:text-primary-foreground [&_svg]:brightness-0 invert',
        cancel:
          'border-secondary bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary [&_svg]:brightness-0 invert',
        link: 'border-secondary bg-secondary text-primary hover:bg-foreground hover:border-foreground hover:text-primary-foreground [&_svg]:brightness-0 invert',
        outline:
          'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground hover:border-foreground active:border-foreground/80',
        lightButton:
          'border-muted bg-muted text-foreground hover:bg-foreground hover:text-primary-foreground hover:border-foreground [&_svg]:brightness-0 invert',
        transparent: 'border-transparent bg-transparent text-foreground',
      },
      size: {
        small: 'h-8 min-w-[132px] px-3 text-sm leading-[1.32]',
        medium: 'h-9 px-3 text-sm',
        large: 'min-w-[200px] h-11 text-lg leading-[1.32]',
      },
      icon: {
        true: 'gap-1 pl-3',
        false: '',
      },
      withSmallPadding: {
        true: 'p-1.5',
        false: '',
      },
      full: {
        true: 'w-full min-w-full',
        false: 'w-fit',
      },
    },
    defaultVariants: {
      size: 'small',
      icon: false,
      withSmallPadding: false,
      full: false,
    },
  },
);

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

type Props = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> & {
  variant: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  href?: string;
  icon?: boolean;
  full?: boolean;
  newTab?: boolean;
  withSmallPadding?: boolean;
  testId?: string;
};

export const Button = ({
  variant,
  children,
  size = 'small',
  href,
  type,
  icon = false,
  full = false,
  withSmallPadding = false,
  testId = 'generic',
  className,
  ...props
}: Props) => {
  const classes = cn(buttonVariants({ variant, size, icon, full, withSmallPadding }), className);
  const dataTestId = formatTestId('button', testId);

  if (href) {
    return (
      <Link
        to={href}
        className={classes}
        target={props.newTab ? '_blank' : '_self'}
        rel={props.newTab ? 'noopener noreferrer' : undefined}
        data-testid={dataTestId}
      >
        {children}
      </Link>
    );
  }

  return (
    <button type={type} className={classes} data-testid={dataTestId} {...props}>
      {children}
    </button>
  );
};
